import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Copy, ExternalLink, Check, Loader2 } from "lucide-react";

interface SendContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerEmail: string;
  contractId: string;
  companyName: string;
  onSent?: (sentAt: string) => void;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export function SendContractDialog({
  open,
  onOpenChange,
  customerName,
  customerEmail,
  contractId,
  companyName,
  onSent,
}: SendContractDialogProps) {
  const [copied, setCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const subject = `${companyName} — Your Service Agreement (${contractId})`;

  const body = `Dear ${customerName},

Thank you for choosing ${companyName}! Your service agreement is attached to this email.

CONTRACT DETAILS:
- Contract ID: ${contractId}
- Date: ${new Date().toLocaleDateString()}

WHAT'S INCLUDED:
- Service Agreement (Terms & Conditions)
- Work Order — Full build breakdown & materials
- Project scope and specifications
- Final pricing summary

NEXT STEPS:
Our team will contact you within 24–48 hours to schedule your installation date.

If you have any questions, please don't hesitate to reach out. We're here to help!

Best regards,
${companyName}

---
This email was sent via Koredor`;

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(customerEmail);
      setCopied(true);
      toast.success("Email address copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy email address");
    }
  };

  const handleOpenMailClient = () => {
    const mailtoLink = `mailto:${customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, "_blank");
  };

  const handleSendEmail = async () => {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      toast.error("Supabase not configured. Use Open Mail Client instead.");
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-contract-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          toEmail: customerEmail,
          subject,
          body,
          customerName,
          companyName,
          contractId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error("Email send failed:", result);
        throw new Error(result.error || "Failed to send email");
      }

      toast.success("Email sent successfully!", {
        description: customerEmail,
      });
      
      if (onSent) {
        onSent(new Date().toISOString());
      }
      onOpenChange(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Try Open Mail Client instead.");
    } finally {
      setIsSending(false);
    }
  };

  const handleMarkAsSent = () => {
    const sentAt = new Date().toISOString();
    if (onSent) {
      onSent(sentAt);
    }
    toast.success("Contract marked as sent", {
      description: customerEmail,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Contract to Homeowner
          </DialogTitle>
          <DialogDescription>
            Review the email below before sending. You can send directly via email or open your default mail client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold">To</Label>
            <div className="flex items-center gap-2">
              <Input value={customerEmail} readOnly className="flex-1" />
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyEmail}
                className="gap-1"
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold">Subject</Label>
            <Input value={subject} readOnly className="text-sm" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold">Message Preview</Label>
            <Textarea
              value={body}
              readOnly
              className="h-48 text-xs font-mono resize-none"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleOpenMailClient}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Open Mail Client
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={isSending}
            className="gap-2"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            {isSending ? "Sending..." : "Send Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
